import React, { useEffect, useCallback, useRef } from 'react';
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import {
  usePrepareTransactionMutation,
  useBroadcastAndTrackMutation,
  useCreateGiftMutation,
} from '../../../../../redux/api/authorized/gifts.js';
import { PHONE_MEDIA_QUERY } from '../../../../../common/responsive.js';
import GiftCard from '../../../../../components/GiftCard/index.jsx';
import { toTokenAmount, useSolana } from '../../../../../hooks/useSolana.js';
import { fromTokenAmount } from '../../../../../hooks/useSolana';
import ConfirmationSkeleton from './skeleton.jsx';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../../../../redux/slices/tokenSlice';
import { selectWalletTokenBalance } from '../../../../../redux/slices/walletSlice';
import { selectTokenBySymbol } from '../../../../../redux/slices/tokenSlice';
import {
  EVENT_GIFTCARD_CREATED,
  EVENT_GIFTCARD_SENT,
  sendEvent,
} from '../../../../../common/analytics.js';
import { formatTokenPrice } from '../../../../../common/currency.js';
import ScrollRestore from '../../../../../components/ScrollRestore/index.tsx';
import { useGiftForm } from '../../../../../components/GiftFormContext/index.jsx';
import Button from '../../../../../components/Button/index.tsx';
import SendingStatus from '../../../../Gifts/SendingStatus/index.jsx';
import { hapticsImpactMedium } from '../../../../../common/haptic.js';
import {
  useCreateOrderQuery,
  useLazyCreateOrderQuery,
} from '../../../../../redux/api/authorized/ultra.ts';

const GiftCardContainer = styled.div`
  width: 417px;
  margin-left: auto;
  margin-right: auto;
  height: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    width: 100%;
  }
`;

const DemoContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-end;
  flex: 1;

  .stripe-onramp-wrapper {
    margin: 0;
    padding: 0;
  }
`;

const Line = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const Message = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.status.error.primary};
  text-align: center;
`;

const SummaryTitle = styled.p`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const SummaryDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 12px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const ContentRow = styled.div`
  display: flex;
  width: 100%;
  gap: 48px;
  color: ${({ theme }) => theme.text.regular};
  flex-direction: column;
`;

const Confirmation = () => {
  const theme = useTheme();
  const [triggerCreateOrder] = useLazyCreateOrderQuery();
  const [createGift] = useCreateGiftMutation();

  const [prepareTransaction] = usePrepareTransactionMutation();
  const [broadcastAndTrack] = useBroadcastAndTrackMutation();

  const [formatedTokenAmount, setFormatedTokenAmount] = useState(0);
  const [formatedAmountUSD, setFormatedAmountUSD] = useState(0);

  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );

  const {
    feedback,
    payingWith,
    network,
    image,
    targetTokenAddress,
    targetTokenAmount,
    delivery,
    email,
    message,
    setFeedback,
    sendingStatus,
    setSendingStatus,
    claimUrl,
    setClaimUrl,
    fromUsdcDirection,
    usdAmount,
  } = useGiftForm();

  const {
    parseSolanaError,
    executeGiftHTLCCreation,
    signAndSerializeTransaction,
    refreshTokenBalances,
    executeUltraSwap,
  } = useSolana();

  const targetToken = useSelector((state) =>
    selectTokenByNetworkAndAddress(state, network, targetTokenAddress),
  );

  const createGiftHandler = async () => {
    try {
      setSendingStatus('sending');

      const giftData = {
        delivery: delivery,
        email: email,
        token: targetToken.address,
        network: network,
        imageUrl: image,
        message: message,
      };

      let giftTokenAmount = 0;

      if (fromUsdcDirection) {
        const orderResult = await triggerCreateOrder({
          inputMint: usdcToken.address,
          outputMint: targetToken.address,
          amount: toTokenAmount(usdAmount, usdcToken.decimals),
        }).unwrap();
        const exactAmountNeeded = orderResult.outAmount;
        giftData.tokenAmount = exactAmountNeeded;
        giftTokenAmount = exactAmountNeeded;
      } else {
        giftData.tokenAmount = toTokenAmount(
          targetTokenAmount,
          targetToken?.decimals,
        );
        giftTokenAmount = targetTokenAmount;
      }

      const needsSwap = payingWith.address !== targetToken.address;

      if (needsSwap) {
        setFeedback('Preparing swap transaction...');

        const orderResult = await triggerCreateOrder({
          inputMint: targetToken.address,
          outputMint: payingWith.address,
          amount: giftTokenAmount,
        }).unwrap();

        const exactAmountNeeded = orderResult.outAmount;

        const swapResponse = await executeUltraSwap({
          inputMint: payingWith.address,
          decimals: payingWith.decimals,
          amount: exactAmountNeeded,
          outputMint: targetToken.address,
        });

        const swapOutAmount =
          swapResponse?.result?.totalOutputAmount ||
          swapResponse?.result?.outputAmountResult;

        if (swapOutAmount) {
          giftData.tokenAmount = swapOutAmount;
          giftTokenAmount = fromTokenAmount(swapOutAmount, targetToken.decimals);
        }
      }

      setFeedback('Creating gift...');

      const giftResponse = await createGift(giftData).unwrap();

      sendEvent(EVENT_GIFTCARD_CREATED, {
        id: giftResponse?.gift?.id,
        token: targetToken,
        base_token: payingWith.address,
        target_token_amount: targetTokenAmount,
        usd_amount: usdAmount,
        created_date: new Date().toISOString(),
      });

      const gift = giftResponse?.gift;

      const isHTLCGift =
        gift?.htlcExpiry || gift?.htlcAccount || gift?.status?.includes('HTLC');

      if (isHTLCGift) {
        const result = await executeGiftHTLCCreation({
          giftId: gift.id,
          outputMint: gift.token,
        });

        if (!result) {
          throw new Error('Gift transaction failed. Please try again.');
        }

        setClaimUrl(result?.gift?.claimUrl);

        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      } else {
        setSendingStatus('sending');
        setFeedback('Preparing gift transaction...');

        const { serializedTransaction } = await prepareTransaction(
          gift.id,
        ).unwrap();

        const base64String = await signAndSerializeTransaction(
          serializedTransaction,
        );

        const result = await broadcastAndTrack({
          giftId: gift.id,
          serializedTransaction: base64String,
        }).unwrap();

        if (!result) {
          throw new Error('Gift transaction failed. Please try again.');
        }

        setClaimUrl(result?.gift?.claimUrl);
        if (result.transactionId) {
          setSendingStatus('success');
          setFeedback(null);
        }
      }

      sendEvent(EVENT_GIFTCARD_SENT, {
        id: gift.id,
        token: gift.token,
        base_token_amount: gift.baseToken,
        base_token: gift.baseTokenAmount,
        created_date: new Date().toISOString(),
      });

      refreshTokenBalances();
      hapticsImpactMedium();
    } catch (error) {
      setSendingStatus('error');
      const errorMessage = parseSolanaError(error);
      setFeedback(`Error: ${errorMessage}`);
    }
  };

  useEffect(() => {
    (async () => {
      if (fromUsdcDirection) {
        console.log('fromUsdcDirection', fromUsdcDirection);
        if (usdcToken && usdAmount && triggerCreateOrder) {
          const orderResult = await triggerCreateOrder({
            inputMint: usdcToken.address,
            outputMint: targetToken.address,
            amount: toTokenAmount(usdAmount, usdcToken.decimals),
          }).unwrap();
          console.log('orderResult', orderResult);
          setFormatedTokenAmount(
            fromTokenAmount(orderResult.outAmount, targetToken.decimals),
          );
          setFormatedAmountUSD(usdAmount);
        }
      } else {
        if (targetToken && targetTokenAmount && triggerCreateOrder) {
          const orderResult = await triggerCreateOrder({
            inputMint: targetToken.address,
            outputMint: usdcToken.address,
            amount: toTokenAmount(targetTokenAmount, targetToken.decimals),
          }).unwrap();
          setFormatedTokenAmount(targetTokenAmount);
          setFormatedAmountUSD(
            fromTokenAmount(orderResult.outAmount, usdcToken.decimals),
          );
        }
      }
    })();
  }, [
    fromUsdcDirection,
    targetTokenAmount,
    usdAmount,
    targetToken,
    usdcToken,
    triggerCreateOrder,
  ]);

  if (!targetToken) {
    return <ConfirmationSkeleton />;
  }

  if (sendingStatus === 'sending' || sendingStatus === 'success') {
    return (
      <>
        <SendingStatus
          giftUrl={claimUrl}
          email={email}
          token={targetToken}
          cardImage={image}
          isSuccess={sendingStatus === 'success'}
          usdAmount={fromUsdcDirection ? formatedAmountUSD : undefined}
          tokenAmount={
            fromUsdcDirection ? formatedTokenAmount : targetTokenAmount
          }
        />
      </>
    );
  }

  return (
    <>
      <ScrollRestore />
      <DemoContainer>
        <ContentRow>
          <GiftCardContainer>
            <GiftCard
              usdAmount={fromUsdcDirection ? usdAmount : undefined}
              tokenAmount={
                !fromUsdcDirection ? targetTokenAmount : formatedTokenAmount
              }
              cardImage={image}
              token={targetToken}
            />
          </GiftCardContainer>
          <FormContainer>
            <SummaryRow>
              <SummaryTitle>
                {fromUsdcDirection
                  ? `$${usdAmount}`
                  : `${targetTokenAmount} ${targetToken.symbol}`}{' '}
                Gift Card
              </SummaryTitle>
              <SummaryTitle>
                {fromUsdcDirection
                  ? `${formatedTokenAmount} ${targetToken.symbol}`
                  : `$${formatedAmountUSD}`}
              </SummaryTitle>
            </SummaryRow>
            {delivery === 'email' && (
              <SummaryRow>
                <SummaryDescription>
                  <span style={{ color: `${theme.text.secondary}` }}>
                    Receiver:
                  </span>{' '}
                  {email}
                </SummaryDescription>
              </SummaryRow>
            )}
            {message && (
              <SummaryRow style={{ flexDirection: 'column' }}>
                <SummaryDescription>
                  <span style={{ color: `${theme.text.secondary}` }}>
                    Message
                  </span>
                </SummaryDescription>
                <SummaryDescription>{message}</SummaryDescription>
              </SummaryRow>
            )}
          </FormContainer>
        </ContentRow>
        <Line />
        <ContentRow>
          <FormContainer>
            <div style={{ display: 'flex', gap: 20, width: '100%' }}>
              <Button fullWidth onClick={createGiftHandler}>
                Purchase Gift
              </Button>
            </div>
            {feedback && <Message>{feedback}</Message>}
          </FormContainer>
        </ContentRow>
      </DemoContainer>
    </>
  );
};

export default Confirmation;
